import { useState, useEffect } from "react";
import {
  Calendar,  Clock, RefreshCw,
  Plus,  ChevronLeft, ChevronRight, TrendingUp, TrendingDown,
  AlertCircle, Search, Filter, Trash2
} from "lucide-react";
import api from "../api/axios";

export default function AttendanceList() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState("Present");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Selection & Pagination
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // Validation
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchEmployees();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (selectedEmployee) fetchAttendance(selectedEmployee);
  }, [selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const res = await api.get("employees/");
      setEmployees(res.data);
      if (res.data.length > 0) setSelectedEmployee(res.data[0].employeeId);
    } catch (err) { console.error(err); }
  };

  const fetchAttendance = async (empId) => {
    setLoading(true);
    try {
      const res = await api.get(`attendance/${empId}/`);
      setRecords(res.data);
      setCurrentPage(1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const validate = () => {
    const newErrors = {};
    if (!selectedEmployee) newErrors.employee = "Please select an employee";
    if (!date) newErrors.date = "Date is required";
    if (new Date(date) > new Date()) newErrors.date = "Cannot mark future attendance";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("attendance/", { employee: selectedEmployee, date, status });
      fetchAttendance(selectedEmployee);
      alert("Attendance recorded!");
    } catch (err) {
      alert("Error saving record.");
    } finally { setSubmitting(false); }
  };

  // Logic: Search & Filter
  const filteredRecords = records.filter(rec => 
    rec.date.includes(searchTerm) || rec.status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const currentRecords = filteredRecords.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

  const toggleSelectAll = () => {
    if (selectedRows.length === currentRecords.length) setSelectedRows([]);
    else setSelectedRows(currentRecords.map(r => r.id));
  };

  const toggleRow = (id) => {
    setSelectedRows(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  // Stats Calculations
  const presentCount = records.filter(r => r.status === 'Present').length;
  const attendanceRate = records.length > 0 ? Math.round((presentCount / records.length) * 100) : 0;
  const currentEmployee = employees.find(e => e.employeeId === selectedEmployee);

  const theme = {
    primary: '#6366f1',
    success: '#10b981',
    danger: '#ef4444',
    bg: '#f8fafc',
    card: '#ffffff'
  };

  return (
    <div style={{ backgroundColor: theme.bg, minHeight: '100vh', padding: isMobile ? '10px' : '30px', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Header Section */}
        <header style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Attendance Dashboard</h1>
            <p style={{ color: '#64748b', fontSize: '14px' }}>Track daily logs and performance metrics</p>
          </div>
          <div style={{ background: '#fff', padding: '8px 15px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '10px' }}>
             <Calendar size={18} color={theme.primary} />
             <span style={{ fontWeight: '600', fontSize: '14px' }}>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>
        </header>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 350px', gap: '25px' }}>
          
          {/* Main Content Area */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
            
            {/* Form Card */}
            <section style={cardStyle}>
              <div style={cardHeaderStyle}><Plus size={18}/> Mark Attendance</div>
              <form onSubmit={handleSubmit} style={{ padding: '20px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '15px', alignItems: 'end' }}>
                <div>
                  <label style={labelStyle}>Employee</label>
                  <select style={inputStyle(errors.employee)} value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)}>
                    {employees.map(emp => <option key={emp.id} value={emp.employeeId}>{emp.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input type="date" style={inputStyle(errors.date)} value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Status</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <select style={inputStyle()} value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="Present">Present</option>
                      <option value="Absent">Absent</option>
                    </select>
                    <button type="submit" disabled={submitting} style={btnPrimaryStyle}>
                      {submitting ? <RefreshCw className="spin" size={18}/> : "Save"}
                    </button>
                  </div>
                </div>
                {errors.date && <p style={{ color: theme.danger, fontSize: '12px', gridColumn: '1/-1', margin: 0 }}>{errors.date}</p>}
              </form>
            </section>

            {/* Attendance List Table/Cards */}
            <section style={cardStyle}>
              <div style={{ ...cardHeaderStyle, justifyContent: 'space-between', display: 'flex' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Clock size={18}/> Recent History</div>
                <div style={{ position: 'relative' }}>
                  <Search style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={14} />
                  <input 
                    type="text" 
                    placeholder="Filter by date..." 
                    style={{ padding: '6px 10px 6px 30px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {loading ? (
                <div style={{ padding: '40px', textAlign: 'center' }}><RefreshCw className="spin" /></div>
              ) : isMobile ? (
                /* Mobile Cards */
                <div style={{ padding: '10px' }}>
                  {currentRecords.map(rec => (
                    <div key={rec.id} style={{ padding: '15px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input type="checkbox" checked={selectedRows.includes(rec.id)} onChange={() => toggleRow(rec.id)} />
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>{rec.date}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>Daily Log</div>
                        </div>
                      </div>
                      <Badge status={rec.status} />
                    </div>
                  ))}
                </div>
              ) : (
                /* Desktop Table */
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                     
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Status</th>
                      
                    </tr>
                  </thead>
                  <tbody>
                    {currentRecords.map(rec => (
                      <tr key={rec.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        
                        <td style={tdStyle}>{new Date(rec.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                        <td style={tdStyle}><Badge status={rec.status} /></td>
                        
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* Pagination */}
              <div style={{ padding: '15px', display: 'flex', justifyContent: 'center', gap: '10px', borderTop: '1px solid #f1f5f9' }}>
                <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={pagBtnStyle}><ChevronLeft size={16}/></button>
                <span style={{ alignSelf: 'center', fontSize: '13px', fontWeight: '600' }}>Page {currentPage} of {totalPages || 1}</span>
                <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} style={pagBtnStyle}><ChevronRight size={16}/></button>
              </div>
            </section>
          </div>

          {/* Sidebar Stats */}
          <aside style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ ...cardStyle, background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)', color: '#fff' }}>
              <div style={{ padding: '20px' }}>
                <h4 style={{ margin: 0, opacity: 0.8, fontSize: '14px' }}>Attendance Rate</h4>
                <div style={{ fontSize: '32px', fontWeight: '800', margin: '10px 0' }}>{attendanceRate}%</div>
              
              </div>
            </div>

            <div style={cardStyle}>
              <div style={cardHeaderStyle}>Quick Insights</div>
              <div style={{ padding: '20px' }}>
                <StatRow label="Present Days" value={presentCount} color={theme.success} />
                <StatRow label="Absent Days" value={records.length - presentCount} color={theme.danger} />
                <StatRow label="Total Records" value={records.length} color="#64748b" />
              </div>
            </div>
          </aside>

        </div>
      </div>

      <style>{`
        .spin { animation: rotation 2s infinite linear; }
        @keyframes rotation { from { transform: rotate(0deg); } to { transform: rotate(359deg); } }
      `}</style>
    </div>
  );
}

// Sub-components
const Badge = ({ status }) => (
  <span style={{
    padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
    backgroundColor: status === 'Present' ? '#dcfce7' : '#fee2e2',
    color: status === 'Present' ? '#166534' : '#991b1b',
    textTransform: 'uppercase'
  }}>{status}</span>
);

const StatRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
    <span style={{ fontSize: '14px', color: '#64748b' }}>{label}</span>
    <span style={{ fontWeight: '700', color: color }}>{value}</span>
  </div>
);

// Styles
const cardStyle = { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' };
const cardHeaderStyle = { padding: '15px 20px', borderBottom: '1px solid #f1f5f9', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' };
const inputStyle = (err) => ({ width: '100%', padding: '10px', borderRadius: '10px', border: `2px solid ${err ? '#ef4444' : '#e2e8f0'}`, fontSize: '14px', outline: 'none', transition: '0.2s', boxSizing: 'border-box' });
const btnPrimaryStyle = { background: '#6366f1', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const thStyle = { padding: '12px 20px', fontSize: '12px', fontWeight: '700', color: '#64748b' };
const tdStyle = { padding: '15px 20px', fontSize: '14px', color: '#1e293b' };
const pagBtnStyle = { padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' };